import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCategory, getCategoryGroups } from '../../../models/categoryModel';
import { toast } from 'react-toastify';

function CategoryCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [formData, setFormData] = useState({
    category_name: '',
    group_id: ''
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await getCategoryGroups();
      if (response.status === 1) {
        setGroups(response.data?.groups || []);
      } else {
        toast.error(response.message || 'Failed to fetch category groups');
      }
    } catch (error) {
      console.error('Error fetching category groups:', error);
      toast.error('An error occurred while fetching category groups');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category_name.trim()) {
      toast.error('Category name is required');
      return;
    }

    if (!formData.group_id) {
      toast.error('Please select a category group');
      return;
    }

    try {
      setLoading(true);
      const response = await createCategory(formData);
      
      if (response.status === 1) {
        toast.success('Category created successfully!');
        navigate('/dashboard/categories');
      } else {
        toast.error(response.message || 'Failed to create category');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('An error occurred while creating the category');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header with Back Navigation */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-[#0087D4] hover:text-blue-600 transition-colors mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Create New Category</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Category Details */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Category Details</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="group_id" className="block text-sm font-medium text-gray-700">
                    Category Group *
                  </label>
                  <select
                    id="group_id"
                    name="group_id"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                    value={formData.group_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a group</option>
                    {groups.map(group => (
                      <option key={group.group_id} value={group.group_id}>
                        {group.group_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
              <div className="flex flex-col gap-3">
                <button 
                  type="submit" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0087D4] hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : 'Create Category'}
                </button>
                <button 
                  type="button" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Category Information */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Category Information</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="category_name" className="block text-sm font-medium text-gray-700">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    id="category_name"
                    name="category_name"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm text-gray-900"
                    value={formData.category_name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter category name"
                  />
                </div>


              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CategoryCreate;